/**
 * VYKA Diamonds — Certificate Verification Tool
 * v1.0
 *
 * Handles:
 * - Local inventory lookup (from Liquid-injected window.__VYKA_CERTS)
 * - Branded redirect for all labs (GIA, IGI, HRD, GCAL)
 * - GIA API proxy (placeholder — activate once partnership is secured)
 * - Barcode / QR scanning (BarcodeDetector + html5-qrcode fallback for Chrome desktop / Firefox)
 * - URL param reading (deep-link to a specific cert)
 * - GA4 analytics event tracking
 * - vyka-rtl-tools-v1 (2026-09-29): Arabic interface via STR/t() (same mechanism as diamond-checker.js),
 *   left-to-right isolation of certificate numbers/measurements in RTL, Arabic links stay in the locale
 */

(function () {
  'use strict';


  /* ---------------------------------------------------------------
     I18N — vyka-rtl-tools-v1 (2026-09-29). Same mechanism as diamond-checker.js:
     LANG from <html lang>, every user-facing string in STR, t() fills {{vars}}.
     English values are the original strings, character for character.
     --------------------------------------------------------------- */
  var LANG = (function () {
    var l = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    return l === 'ar' ? 'ar' : 'en';
  })();

  var STR = {
    'err.empty':     { en: 'Please enter a certificate number.', ar: 'رقم الشهادة مطلوب.' },
    'err.length':    { en: 'Certificate numbers are typically 5\u201310 digits.', ar: 'يتكوّن رقم الشهادة عادةً من 5 إلى 10 أرقام.' },
    'err.lab':       { en: 'Please select a grading laboratory.', ar: 'مختبر التصنيف غير محدَّد.' },
    'doc.title':     { en: 'Verify {{lab}} Certificate #{{num}} \u2014 VYKA Diamonds', ar: 'التحقق من شهادة {{lab}} رقم {{num}} \u2014 VYKA' },
    'res.verified':  { en: 'Certificate Verified', ar: 'تم التحقق من الشهادة' },
    'res.certNo':    { en: '{{lab}} Certificate #{{num}}', ar: 'شهادة {{lab}} رقم {{num}}' },
    'c4.carat':      { en: 'Carat', ar: 'القيراط' },
    'c4.cut':        { en: 'Cut', ar: 'القطع' },
    'c4.colour':     { en: 'Colour', ar: 'اللون' },
    'c4.clarity':    { en: 'Clarity', ar: 'النقاء' },
    'unit.ct':       { en: 'ct', ar: 'قيراط' },
    'spec.title':    { en: 'Detailed Specifications', ar: 'المواصفات التفصيلية' },
    'spec.shape':    { en: 'Shape', ar: 'الشكل' },
    'spec.meas':     { en: 'Measurements', ar: 'القياسات' },
    'spec.polish':   { en: 'Polish', ar: 'التلميع' },
    'spec.sym':      { en: 'Symmetry', ar: 'التناظر' },
    'spec.fluor':    { en: 'Fluorescence', ar: 'الفلورية' },
    'spec.depth':    { en: 'Depth', ar: 'العمق' },
    'spec.table':    { en: 'Table', ar: 'الطاولة' },
    'spec.date':     { en: 'Report Date', ar: 'تاريخ التقرير' },
    'cta.available': { en: 'This diamond is available at VYKA', ar: 'هذه الألماسة متوفرة لدى VYKA' },
    'cta.view':      { en: 'View This Diamond', ar: 'عرض التفاصيل' },
    'cta.sold':      { en: 'This diamond was previously available at VYKA and has been sold.', ar: 'كانت هذه الألماسة معروضة لدى VYKA وقد بِيعت.' },
    'cta.similar':   { en: 'Browse Similar Diamonds', ar: 'تصفُّح خواتم الخطوبة' },
    'cta.notInv':    { en: 'This diamond is not currently in our inventory.', ar: 'هذه الألماسة ليست ضمن مخزون VYKA حاليًا.' },
    'lab.issued':    { en: 'Certificate issued by {{lab}}. ', ar: 'الشهادة صادرة عن {{lab}}. ' },
    'lab.also':      { en: 'You can also ', ar: 'ويمكن أيضًا ' },
    'lab.direct':    { en: 'verify directly at {{lab}}', ar: 'التحقق منها مباشرةً لدى {{lab}}' },
    'rd.title':      { en: 'Redirecting to {{lab}}', ar: 'جارٍ التحويل إلى {{lab}}' },
    'rd.body':       { en: 'This certificate was issued by {{lab}}. We\u2019ll take you to their official verification portal to view the full report.', ar: 'صدرت هذه الشهادة عن {{lab}}، ويُعرض التقرير الكامل على بوابة التحقق الرسمية للمختبر.' },
    'rd.copyTitle':  { en: 'Click to copy', ar: 'نسخ الرقم' },
    'rd.copied':     { en: 'Copied!', ar: 'تم النسخ' },
    'rd.go':         { en: 'Go to {{lab}} Now', ar: 'الانتقال إلى {{lab}}' },
    'rd.looking':    { en: 'Looking for a certified diamond? Browse our collection.', ar: 'مجموعة VYKA من الألماس المعتمد.' },
    'rd.browse':     { en: 'Browse VYKA Diamonds', ar: 'تصفُّح خواتم الخطوبة' },
    'er.title':      { en: 'Certificate Not Found', ar: 'لم يُعثر على الشهادة' },
    'er.persist':    { en: 'If the issue persists, verify directly with the lab:', ar: 'وعند استمرار المشكلة، يمكن التحقق مباشرةً لدى المختبر:' },
    'er.verifyAt':   { en: 'Verify at {{lab}}', ar: 'التحقق لدى {{lab}}' },
    'er.lab':        { en: 'Lab', ar: 'المختبر' },
    'er.retry':      { en: 'Try Again', ar: 'إعادة المحاولة' },
    'sc.dialog':     { en: 'Barcode scanner', ar: 'ماسح الباركود' },
    'sc.close':      { en: 'Close scanner', ar: 'إغلاق الماسح' },
    'sc.hint':       { en: 'Point your camera at the barcode on the certificate', ar: 'توجيه الكاميرا نحو الباركود المطبوع على الشهادة' },
    'sc.trouble':    { en: 'Having trouble? ', ar: 'تعذّر المسح؟ ' },
    'sc.manual':     { en: 'Enter the number manually', ar: 'إدخال الرقم يدويًا' },
    'sc.slow':       { en: 'Having trouble detecting the barcode?', ar: 'تعذّر التعرّف على الباركود؟' },
    'sc.camera':     { en: 'Camera access is required for barcode scanning. Please allow camera permissions and try again.', ar: 'يتطلب مسح الباركود إذنًا بالوصول إلى الكاميرا. يلزم منح الإذن ثم إعادة المحاولة.' },
    'sc.loading':    { en: 'Loading scanner\u2026', ar: 'جارٍ تحميل الماسح\u2026' },
    'sc.noCamera':   { en: 'Unable to access camera. Please enter the number manually.', ar: 'تعذّر الوصول إلى الكاميرا. يمكن إدخال الرقم يدويًا.' },
    'sc.noLoad':     { en: 'Scanner could not be loaded. Please enter the number manually.', ar: 'تعذّر تحميل الماسح. يمكن إدخال الرقم يدويًا.' }
  };

  function t(key, vars) {
    var row = STR[key];
    var str = row ? (row[LANG] || row.en || '') : key;
    if (vars) for (var k in vars) str = str.replace(new RegExp('\\{\\{' + k + '\\}\\}', 'g'), String(vars[k]));
    return str;
  }

  /* Arabic display names for grades and certificate values (display only; lookups use the English value) */
  var VAL_AR = {
    'excellent':'ممتاز', 'very good':'جيد جدًا', 'good':'جيد', 'fair':'مقبول', 'poor':'ضعيف',
    'none':'لا يوجد', 'faint':'خفيف', 'medium':'متوسط', 'strong':'قوي', 'very strong':'قوي جدًا',
    'round':'مستدير', 'round brilliant':'مستدير', 'princess':'برنسيس', 'cushion':'كوشن', 'cushion brilliant':'كوشن',
    'cushion modified':'كوشن', 'oval':'بيضاوي', 'emerald':'إمرلد', 'square emerald':'إمرلد مربّع', 'pear':'كمثرى',
    'heart':'قلب', 'radiant':'راديانت', 'marquise':'ماركيز', 'asscher':'آشر', 'trillion':'تريليون', 'baguette':'باغيت',
    'natural':'طبيعي', 'lab grown':'مختبري', 'lab-grown':'مختبري', 'lab grown diamond':'ألماس مختبري', 'natural diamond':'ألماس طبيعي',
    'loose diamond':'ألماسة سائبة', 'engagement ring':'خاتم خطوبة', 'engagement rings':'خواتم خطوبة', 'ring':'خاتم',
    'earrings':'أقراط', 'pendant':'قلادة', 'necklace':'عقد', 'bracelet':'سوار'
  };
  function vl(v) {
    if (LANG !== 'ar' || v == null) return v;
    var k = String(v).trim().toLowerCase();
    return VAL_AR[k] || v;
  }
  var GRADE_INFO_AR = {
    D: 'عديم اللون تمامًا — أعلى درجة',
    E: 'عديم اللون — آثار دقيقة لا يلحظها إلا الخبراء',
    F: 'عديم اللون — لون طفيف يرصده الخبراء',
    G: 'شبه عديم اللون — يصعب رصد اللون',
    H: 'شبه عديم اللون — لون طفيف ملحوظ',
    I: 'شبه عديم اللون — لون يمكن رصده قليلًا',
    J: 'شبه عديم اللون — لون طفيف مرئي',
    FL:   'خالٍ من العيوب — لا شوائب ولا عيوب سطحية',
    IF:   'خالٍ من العيوب داخليًا — لا شوائب مرئية',
    VVS1: 'شوائب ضئيلة للغاية — يتعذّر رصدها تقريبًا',
    VVS2: 'شوائب ضئيلة للغاية — يصعب رصدها كثيرًا',
    VS1:  'شوائب طفيفة جدًا — شوائب ثانوية',
    VS2:  'شوائب طفيفة جدًا — شوائب ثانوية تُرى بالتكبير',
    SI1:  'شوائب طفيفة — ملحوظة بالتكبير',
    SI2:  'شوائب طفيفة — ملحوظة بسهولة بالتكبير',
    I1:   'شوائب — مرئية بالعين المجرّدة',
    I2:   'شوائب — مرئية بسهولة',
    I3:   'شوائب — واضحة جدًا',
    Excellent:  'أقصى قدر من النار واللمعان',
    'Very Good': 'لمعان ونار متميزان',
    Good:       'لمعان جيد — تسرّب طفيف للضوء',
    Fair:       'بعض اللمعان — تسرّب ملحوظ للضوء',
    Poor:       'لمعان أو نار ضئيلان'
  };
  /* Arabic only: Latin/numeric values (certificate numbers, measurements, dates, lab names) keep
     left-to-right order inside right-to-left text. English output is plain esc(). */
  function ltr(v) {
    var e = esc(v);
    if (LANG !== 'ar' || !e || /[\u0600-\u06FF]/.test(String(v))) return e;
    return '<bdi dir="ltr">' + e + '</bdi>';
  }
  /* Arabic only: '9.12 - 6.40 x 3.95 mm' -> numbers isolated left-to-right + «مم»; null in English (the caller keeps ltr()) */
  function arMeas(v) {
    if (LANG !== 'ar' || !v) return null;
    var m = /^(.*?\d)\s*mm\.?\s*$/i.exec(String(v).trim());
    return m ? ltr(m[1]) + ' مم' : null;
  }
  /* Arabic only: '2026-03-14' or 'March 14, 2026' -> «14 مارس 2026» (Gregorian, Western digits); anything unparseable is left as stored */
  function arDate(v) {
    if (LANG !== 'ar' || !v) return v;
    var s = String(v).trim(), m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s), d;
    if (m) d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    else if (/[A-Za-z]/.test(s) && /\d{4}/.test(s)) { var ms = Date.parse(s); if (isNaN(ms)) return v; var l = new Date(ms); d = new Date(Date.UTC(l.getFullYear(), l.getMonth(), l.getDate())); }
    else return v;
    try {
      return new Intl.DateTimeFormat('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
        .format(d).replace(/[\u200e\u200f\u061c]/g, '').replace(/\s*م$/, '');
    } catch (e) { return v; }
  }
  function arSeconds(n) {
    if (n === 2) return 'ثانيتين';
    if (n === 1) return 'ثانية واحدة';
    if (n >= 3 && n <= 10) return n + ' ثوانٍ';
    return n + ' ثانية';
  }
  /* Arabic links stay inside the /ar-sa locale; English keeps its original root-relative paths */
  function localPath(p) {
    if (LANG !== 'ar') return p;
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/$/, '') + p;
  }

  /* ---------------------------------------------------------------
     CONSTANTS
     --------------------------------------------------------------- */
  const LABS = {
    gia:  { name: 'GIA',          url: 'https://www.gia.edu/report-check-landing',    prefill: (n) => `https://www.gia.edu/report-check-landing?reportno=${n}` },
    igi:  { name: 'IGI',          url: 'https://www.igi.org/verify-your-report',      prefill: (n) => `https://www.igi.org/verify-your-report?r=${n}` },
    hrd:  { name: 'HRD Antwerp',  url: 'https://my.hrdantwerp.com',                  prefill: null },
    gcal: { name: 'GCAL',         url: 'https://www.gcalusa.com/certificate.html',    prefill: (n) => `https://www.gcalusa.com/certificate.html?certNo=${n}` }
  };

  /* Human-readable explanations for grades */
  const GRADE_INFO = {
    /* Colour */
    D: 'Completely colourless — the highest grade',
    E: 'Colourless — minute traces only seen by experts',
    F: 'Colourless — slight colour detected by experts',
    G: 'Near colourless — colour difficult to detect',
    H: 'Near colourless — slight colour noticeable',
    I: 'Near colourless — slightly detectable colour',
    J: 'Near colourless — slight colour visible',
    /* Clarity */
    FL:   'Flawless — no inclusions or blemishes',
    IF:   'Internally Flawless — no inclusions visible',
    VVS1: 'Very Very Slightly Included — extremely difficult to see',
    VVS2: 'Very Very Slightly Included — very difficult to see',
    VS1:  'Very Slightly Included — minor inclusions',
    VS2:  'Very Slightly Included — minor inclusions visible under magnification',
    SI1:  'Slightly Included — noticeable under magnification',
    SI2:  'Slightly Included — easily noticeable under magnification',
    I1:   'Included — visible to the naked eye',
    I2:   'Included — easily visible',
    I3:   'Included — obvious inclusions',
    /* Cut */
    Excellent:  'Maximum fire and brilliance',
    'Very Good': 'Outstanding brilliance and fire',
    Good:       'Good brilliance — slight light leakage',
    Fair:       'Some brilliance — noticeable light leakage',
    Poor:       'Little brilliance or fire'
  };

  /* Lab detection patterns */
  const LAB_PATTERNS = [
    { lab: 'igi',  regex: /^LG\d{7,9}$/i,       priority: 10 },
    { lab: 'gia',  regex: /^\d{10}$/,            priority: 5, firstDigit: '12567' },
    { lab: 'igi',  regex: /^\d{7,9}$/,           priority: 3 },
    { lab: 'hrd',  regex: /^\d{8}$/,             priority: 1 },
    { lab: 'gcal', regex: /^\d{5,6}$/,           priority: 2 }
  ];

  /* ---------------------------------------------------------------
     HELPERS
     --------------------------------------------------------------- */

  /** Track GA4 event */
  function trackEvent(name, params) {
    if (typeof gtag === 'function') {
      gtag('event', name, params || {});
    }
  }

  /** Auto-detect lab from certificate number format */
  function detectLab(num) {
    var clean = num.trim();
    var matches = [];
    for (var i = 0; i < LAB_PATTERNS.length; i++) {
      var p = LAB_PATTERNS[i];
      if (p.regex.test(clean)) {
        if (p.firstDigit && p.firstDigit.indexOf(clean[0]) === -1) continue;
        matches.push(p);
      }
    }
    if (matches.length === 0) return '';
    matches.sort(function (a, b) { return b.priority - a.priority; });
    return matches[0].lab;
  }

  /** Format price from cents to AED string */
  function formatPrice(cents) {
    if (!cents && cents !== 0) return '';
    var amount = (parseInt(cents, 10) / 100).toLocaleString('en-AE', { minimumFractionDigits: 0 });
    return 'AED ' + amount;
  }

  /** Escape HTML */
  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  /* ---------------------------------------------------------------
     SVG ICONS
     --------------------------------------------------------------- */
  var ICONS = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    redirect: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>',
    error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
    scan: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 012-2h2"/><path d="M17 3h2a2 2 0 012 2v2"/><path d="M21 17v2a2 2 0 01-2 2h-2"/><path d="M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>',
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    chevron: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>'
  };

  /* ---------------------------------------------------------------
     MAIN: CertificateVerifier
     --------------------------------------------------------------- */
  function CertificateVerifier() {
    this.form        = document.getElementById('cert-verify-form');
    this.labSelect   = document.getElementById('cert-lab');
    this.numberInput = document.getElementById('cert-number');
    this.resultsEl   = document.getElementById('cert-results');
    this.scanBtn     = document.getElementById('cert-scan-btn');
    this.certs       = window.__VYKA_CERTS || {};
    this.scanner     = null;
    this._redirectTimer = null;
    this._labManuallySelected = false;

    this._bindEvents();
    this._checkUrlParams();
  }

  CertificateVerifier.prototype._bindEvents = function () {
    var self = this;

    /* Form submit */
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self._handleSubmit();
    });

    /* Track manual lab selection */
    this.labSelect.addEventListener('change', function () {
      self._labManuallySelected = self.labSelect.value !== '';
    });

    /* Auto-detect lab on input (re-detects as user types unless manually overridden) */
    this.numberInput.addEventListener('input', function () {
      self._clearFieldError('cert-number');
      var num = self.numberInput.value.trim();
      if (num.length >= 5 && !self._labManuallySelected) {
        var detected = detectLab(num);
        self.labSelect.value = detected || '';
      }
    });

    /* Scan button */
    if (this.scanBtn) {
      this.scanBtn.addEventListener('click', function () {
        self._startScanner();
      });
    }
  };

  /* ----- URL Params ----- */
  CertificateVerifier.prototype._checkUrlParams = function () {
    var params = new URLSearchParams(window.location.search);
    var lab  = (params.get('lab') || '').toLowerCase();
    var cert = params.get('cert') || '';
    if (cert) {
      if (lab && this.labSelect.querySelector('option[value="' + lab + '"]')) {
        this.labSelect.value = lab;
      }
      this.numberInput.value = cert;
      this._handleSubmit();
    }
  };

  /* ----- Submit ----- */
  CertificateVerifier.prototype._handleSubmit = function () {
    var num = this.numberInput.value.trim().replace(/\s+/g, '');
    var lab = this.labSelect.value;

    /* Validate */
    if (!num) {
      this._showFieldError('cert-number', t('err.empty'));
      this.numberInput.focus();
      return;
    }
    if (num.length < 5 || num.length > 15) {
      this._showFieldError('cert-number', t('err.length'));
      this.numberInput.focus();
      return;
    }

    /* Auto-detect lab if not selected */
    if (!lab) {
      lab = detectLab(num);
      if (lab) this.labSelect.value = lab;
    }

    if (!lab) {
      this._showFieldError('cert-lab', t('err.lab'));
      this.labSelect.focus();
      return;
    }

    /* Track event */
    trackEvent('cert_verify_submitted', { lab: lab, cert_number: num, input_method: 'manual' });

    /* Update URL */
    this._updateUrl(lab, num);

    /* Show skeleton */
    this._showSkeleton();

    /* Lookup */
    var self = this;
    /* Small delay for skeleton to render */
    setTimeout(function () { self._lookup(lab, num); }, 300);
  };

  /* ----- Lookup Logic ----- */
  CertificateVerifier.prototype._lookup = function (lab, num) {
    /* 1. Check local inventory */
    var localResult = this.certs[num];
    if (localResult) {
      trackEvent('cert_verify_found', { lab: lab, cert_number: num, source: 'vyka_inventory' });
      this._renderResults(localResult);
      return;
    }

    /* 2. Check with uppercase/lowercase variations */
    var altKeys = [num.toUpperCase(), num.toLowerCase(), 'LG' + num, num.replace(/^LG/i, '')];
    for (var i = 0; i < altKeys.length; i++) {
      if (this.certs[altKeys[i]]) {
        trackEvent('cert_verify_found', { lab: lab, cert_number: num, source: 'vyka_inventory' });
        this._renderResults(this.certs[altKeys[i]]);
        return;
      }
    }

    /* 3. TODO: GIA API proxy (uncomment when proxy is deployed)
    if (lab === 'gia') {
      this._fetchGIA(num);
      return;
    }
    */

    /* 4. Redirect for all non-inventory certs */
    trackEvent('cert_verify_redirect', { lab: lab, cert_number: num, redirect_url: LABS[lab].url });
    this._renderRedirect(lab, num);
  };

  /* ----- GIA API (placeholder) ----- */
  /*
  CertificateVerifier.prototype._fetchGIA = function (num) {
    var self = this;
    fetch('/api/verify?lab=gia&cert=' + encodeURIComponent(num))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status === 'found') {
          trackEvent('cert_verify_found', { lab: 'gia', cert_number: num, source: data.source });
          self._renderResults(data.data, data.vyka_product);
        } else {
          self._renderError(num, 'gia');
        }
      })
      .catch(function () {
        self._renderError(num, 'gia');
      });
  };
  */

  /* ----- URL Management ----- */
  CertificateVerifier.prototype._updateUrl = function (lab, num) {
    var url = new URL(window.location);
    url.searchParams.set('lab', lab);
    url.searchParams.set('cert', num);
    history.pushState({}, '', url);

    /* Update meta tags for sharing */
    var labInfo = LABS[lab] || {};
    document.title = t('doc.title', { lab: (labInfo.name || lab.toUpperCase()), num: num });
  };

  /* ----- Field Errors ----- */
  CertificateVerifier.prototype._showFieldError = function (fieldId, msg) {
    var field = document.getElementById(fieldId);
    if (!field) return;
    var wrapper = field.closest('.cert-verify__field');
    if (!wrapper) return;
    wrapper.classList.add('cert-verify__field--error');
    var errEl = wrapper.querySelector('.cert-verify__field-error');
    if (errEl) errEl.textContent = msg;
    field.setAttribute('aria-describedby', fieldId + '-error');
  };

  CertificateVerifier.prototype._clearFieldError = function (fieldId) {
    var field = document.getElementById(fieldId);
    if (!field) return;
    var wrapper = field.closest('.cert-verify__field');
    if (wrapper) wrapper.classList.remove('cert-verify__field--error');
  };

  /* ---------------------------------------------------------------
     RENDER: Skeleton
     --------------------------------------------------------------- */
  CertificateVerifier.prototype._showSkeleton = function () {
    this.resultsEl.hidden = false;
    this.resultsEl.setAttribute('aria-busy', 'true');
    this.resultsEl.innerHTML =
      '<div class="cert-verify__skeleton">' +
        '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--header"></div>' +
        '<div class="cert-verify__skeleton-row">' +
          '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--card"></div>' +
          '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--card"></div>' +
          '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--card"></div>' +
          '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--card"></div>' +
        '</div>' +
        '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--line"></div>' +
        '<div class="cert-verify__skeleton-block cert-verify__skeleton-block--line-short" style="margin-top:8px"></div>' +
      '</div>';
  };

  /* ---------------------------------------------------------------
     RENDER: Results (VYKA inventory match)
     --------------------------------------------------------------- */
  CertificateVerifier.prototype._renderResults = function (cert, vykaProduct) {
    var lab = (cert.lab || '').toLowerCase();
    var labInfo = LABS[lab] || {};
    var isSold = cert.available === false;

    var html = '<div class="cert-verify__results-inner">';

    /* Header */
    html += '<div class="cert-verify__result-header">' +
      '<div class="cert-verify__result-check">' + ICONS.check + '</div>' +
      '<div class="cert-verify__result-header-text">' +
        '<h2>' + esc(t('res.verified')) + '</h2>' +
        '<p>' + t('res.certNo', { lab: ltr(labInfo.name || cert.lab), num: ltr(cert.number || cert.cert_number) }) +
        (cert.stone_type ? ' &middot; ' + ltr(vl(cert.stone_type)) : '') + '</p>' +
      '</div>' +
    '</div>';

    /* 4C Cards */
    html += '<div class="cert-verify__4c-grid">';
    html += this._render4CCard(t('c4.carat'), cert.carat, cert.carat ? cert.carat + ' ' + t('unit.ct') : '\u2014');
    html += this._render4CCard(t('c4.cut'), cert.cut, cert.cut ? vl(cert.cut) : '\u2014');
    html += this._render4CCard(t('c4.colour'), cert.colour, cert.colour || '\u2014');
    html += this._render4CCard(t('c4.clarity'), cert.clarity, cert.clarity || '\u2014');
    html += '</div>';

    /* Specs Table */
    html += '<div class="cert-verify__specs">';
    html += '<h3 class="cert-verify__specs-title">' + esc(t('spec.title')) + '</h3>';
    html += '<table class="cert-verify__specs-table"><tbody>';

    var specs = [
      [t('spec.shape'), vl(cert.shape)],
      [t('spec.meas'),  cert.measurements, arMeas(cert.measurements)],
      [t('spec.polish'), vl(cert.polish)],
      [t('spec.sym'),   vl(cert.symmetry)],
      [t('spec.fluor'), vl(cert.fluorescence)],
      [t('spec.depth'), cert.depth ? cert.depth + '%' : ''],
      [t('spec.table'), cert.table ? cert.table + '%' : ''],
      [t('spec.date'),  arDate(cert.report_date)]
    ];
    for (var i = 0; i < specs.length; i++) {
      if (specs[i][1]) {
        html += '<tr><td>' + esc(specs[i][0]) + '</td><td>' + (specs[i][2] || ltr(specs[i][1])) + '</td></tr>';
      }
    }
    html += '</tbody></table></div>';

    /* Product CTA */
    if (cert.product_url && !isSold) {
      var price = cert.price_formatted || formatPrice(cert.price);
      html += '<div class="cert-verify__cta">' +
        '<div class="cert-verify__cta-text">' +
          '<h3>' + esc(t('cta.available')) + '</h3>' +
          '<p>' + esc(cert.product_title || '') + '</p>' +
        '</div>' +
        (price ? '<span class="cert-verify__cta-price">' + esc(price) + '</span>' : '') +
        '<a href="' + esc(cert.product_url) + '" class="cert-verify__btn cert-verify__btn--gold" ' +
          'onclick="typeof gtag===\'function\'&&gtag(\'event\',\'cert_verify_product_click\',{lab:\'' + lab + '\',product_url:\'' + esc(cert.product_url) + '\'})">' +
          esc(t('cta.view')) + '</a>' +
      '</div>';
    } else if (isSold) {
      html += '<div class="cert-verify__similar">' +
        '<p>' + esc(t('cta.sold')) + '</p>' +
        '<a href="' + esc(localPath('/collections/engagement-rings')) + '" class="cert-verify__btn cert-verify__btn--primary" ' +
          'onclick="typeof gtag===\'function\'&&gtag(\'event\',\'cert_verify_similar_click\')">' +
          esc(t('cta.similar')) + '</a>' +
      '</div>';
    } else {
      html += '<div class="cert-verify__similar">' +
        '<p>' + esc(t('cta.notInv')) + '</p>' +
        '<a href="' + esc(localPath('/collections/engagement-rings')) + '" class="cert-verify__btn cert-verify__btn--primary" ' +
          'onclick="typeof gtag===\'function\'&&gtag(\'event\',\'cert_verify_similar_click\')">' +
          esc(t('cta.similar')) + '</a>' +
      '</div>';
    }

    /* Lab link */
    html += '<div class="cert-verify__lab-link">' +
      t('lab.issued', { lab: ltr(labInfo.name || cert.lab) }) +
      esc(t('lab.also')) + '<a href="' + esc(labInfo.url || '#') + '" target="_blank" rel="noopener">' +
      t('lab.direct', { lab: ltr(labInfo.name || cert.lab) }) + '</a>.' +
    '</div>';

    html += '</div>';

    this.resultsEl.innerHTML = html;
    this.resultsEl.setAttribute('aria-busy', 'false');
    this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  CertificateVerifier.prototype._render4CCard = function (label, gradeKey, displayValue) {
    var GI = LANG === 'ar' ? GRADE_INFO_AR : GRADE_INFO;
    var desc = GI[gradeKey] || GI[displayValue] || '';
    return '<div class="cert-verify__4c-card">' +
      '<p class="cert-verify__4c-label">' + esc(label) + '</p>' +
      '<p class="cert-verify__4c-value">' + ltr(displayValue) + '</p>' +
      (desc ? '<p class="cert-verify__4c-desc">' + esc(desc) + '</p>' : '') +
    '</div>';
  };

  /* ---------------------------------------------------------------
     RENDER: Redirect (non-API lab, non-inventory)
     --------------------------------------------------------------- */
  CertificateVerifier.prototype._renderRedirect = function (lab, num) {
    var labInfo = LABS[lab] || {};
    var redirectUrl = labInfo.prefill ? labInfo.prefill(num) : labInfo.url;
    var canPrefill = !!labInfo.prefill;

    var html = '<div class="cert-verify__redirect">' +
      '<div class="cert-verify__redirect-icon">' + ICONS.redirect + '</div>' +
      '<h2>' + t('rd.title', { lab: ltr(labInfo.name) }) + '</h2>' +
      '<p>' + t('rd.body', { lab: ltr(labInfo.name) }) + '</p>';

    if (canPrefill) {
      html += LANG === 'ar'
        ? '<p class="cert-verify__redirect-timer">التحويل خلال <span id="cert-countdown">' + arSeconds(5) + '</span>\u2026</p>'
        : '<p class="cert-verify__redirect-timer">Redirecting in <span id="cert-countdown">5</span> seconds\u2026</p>';
    }

    /* Show cert number with copy button (especially important for HRD which has no prefill) */
    html += '<div class="cert-verify__redirect-copy" id="cert-copy-btn" title="' + esc(t('rd.copyTitle')) + '">' +
      '<span' + (LANG === 'ar' ? ' dir="ltr"' : '') + '>' + esc(num) + '</span>' + ICONS.copy +
    '</div>';

    html += '<div class="cert-verify__redirect-actions">' +
      '<a href="' + esc(redirectUrl) + '" target="_blank" rel="noopener" class="cert-verify__btn cert-verify__btn--primary" id="cert-redirect-link">' +
      esc(t('rd.go', { lab: labInfo.name })) + '</a>' +   /* buttons are flex boxes: a <bdi> would become its own flex item */
    '</div>';

    html += '<hr class="cert-verify__redirect-divider">';
    html += '<div class="cert-verify__similar">' +
      '<p>' + esc(t('rd.looking')) + '</p>' +
      '<a href="' + esc(localPath('/collections/engagement-rings')) + '" class="cert-verify__btn cert-verify__btn--secondary" ' +
        'onclick="typeof gtag===\'function\'&&gtag(\'event\',\'cert_verify_similar_click\')">' +
        esc(t('rd.browse')) + '</a>' +
    '</div>';

    html += '</div>';

    this.resultsEl.innerHTML = html;
    this.resultsEl.hidden = false;
    this.resultsEl.setAttribute('aria-busy', 'false');
    this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

    /* Copy button */
    var copyBtn = document.getElementById('cert-copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        navigator.clipboard.writeText(num).then(function () {
          copyBtn.classList.add('cert-verify__redirect-copy--copied');
          copyBtn.querySelector('span').textContent = t('rd.copied');
          setTimeout(function () {
            copyBtn.classList.remove('cert-verify__redirect-copy--copied');
            copyBtn.querySelector('span').textContent = num;
          }, 2000);
        });
      });
    }

    /* Auto-redirect countdown (only for labs with prefill) */
    if (canPrefill) {
      var countdown = 5;
      var timerEl = document.getElementById('cert-countdown');
      var self = this;
      this._redirectTimer = setInterval(function () {
        countdown--;
        if (timerEl) {
          if (LANG === 'ar' && countdown <= 0 && timerEl.parentNode) timerEl.parentNode.textContent = 'جارٍ التحويل\u2026';
          else timerEl.textContent = LANG === 'ar' ? arSeconds(countdown) : countdown;
        }
        if (countdown <= 0) {
          clearInterval(self._redirectTimer);
          window.open(redirectUrl, '_blank', 'noopener');
        }
      }, 1000);
    }
  };

  /* ---------------------------------------------------------------
     RENDER: Error
     --------------------------------------------------------------- */
  CertificateVerifier.prototype._renderError = function (num, lab) {
    var labInfo = LABS[lab] || {};

    trackEvent('cert_verify_not_found', { lab: lab, cert_number: num });

    var html = '<div class="cert-verify__error">' +
      '<div class="cert-verify__error-icon">' + ICONS.error + '</div>' +
      '<h2>' + esc(t('er.title')) + '</h2>' +
      (LANG === 'ar'
        ? '<p>لم يُعثر على الشهادة رقم <strong dir="ltr">' + esc(num) + '</strong>' +
          (labInfo.name ? ' في قاعدة بيانات ' + ltr(labInfo.name) : '') + '. ' +
          'يلزم التأكد من الرقم ومن المختبر المحدَّد.</p>'
        : '<p>We couldn\u2019t find certificate <strong>' + esc(num) + '</strong>' +
          (labInfo.name ? ' in ' + esc(labInfo.name) + '\u2019s database' : '') + '. ' +
          'Please double-check the number and selected laboratory.</p>') +
      '<p>' + esc(t('er.persist')) + '</p>' +
      '<a href="' + esc(labInfo.url || '#') + '" target="_blank" rel="noopener" class="cert-verify__btn cert-verify__btn--primary">' +
        esc(t('er.verifyAt', { lab: labInfo.name || t('er.lab') })) + '</a>' +
      '<div style="margin-top:24px">' +
        '<button type="button" class="cert-verify__btn cert-verify__btn--secondary" onclick="document.getElementById(\'cert-number\').focus();document.getElementById(\'cert-results\').hidden=true;">' +
          esc(t('er.retry')) + '</button>' +
      '</div>' +
    '</div>';

    this.resultsEl.innerHTML = html;
    this.resultsEl.hidden = false;
    this.resultsEl.setAttribute('aria-busy', 'false');
    this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ---------------------------------------------------------------
     BARCODE SCANNER
     --------------------------------------------------------------- */
  CertificateVerifier.prototype._startScanner = function () {
    var self = this;
    trackEvent('cert_verify_scan_start', { device_type: /iPhone|iPad/i.test(navigator.userAgent) ? 'iOS' : 'Android' });

    /* Build modal */
    var overlay = document.createElement('div');
    overlay.className = 'cert-verify__scanner-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', t('sc.dialog'));
    overlay.innerHTML =
      '<div class="cert-verify__scanner-container">' +
        '<video autoplay playsinline muted></video>' +
        '<div class="cert-verify__scanner-crosshair"></div>' +
        '<button class="cert-verify__scanner-close" aria-label="' + esc(t('sc.close')) + '">' + ICONS.close + '</button>' +
      '</div>' +
      '<p class="cert-verify__scanner-hint">' + esc(t('sc.hint')) + '</p>' +
      '<p class="cert-verify__scanner-fallback">' + esc(t('sc.trouble')) + '<a id="cert-scan-manual">' + esc(t('sc.manual')) + '</a></p>';

    document.body.appendChild(overlay);

    var video  = overlay.querySelector('video');
    var closeBtn = overlay.querySelector('.cert-verify__scanner-close');
    var manualLink = document.getElementById('cert-scan-manual');
    var stream = null;
    var scanInterval = null;
    var scanTimeout = null;

    function cleanup() {
      if (scanInterval) clearInterval(scanInterval);
      if (scanTimeout) clearTimeout(scanTimeout);
      if (self._fallbackReader) {
        try { self._fallbackReader.stop().catch(function () {}); } catch (e) {}
        self._fallbackReader = null;
      }
      if (stream) stream.getTracks().forEach(function (t) { t.stop(); });
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      self.numberInput.focus();
    }

    closeBtn.addEventListener('click', cleanup);
    manualLink.addEventListener('click', function (e) {
      e.preventDefault();
      trackEvent('cert_verify_scan_fallback', {});
      cleanup();
    });

    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') cleanup();
    });

    if ('BarcodeDetector' in window) {
      /* ===== NATIVE PATH: getUserMedia + BarcodeDetector (Chrome Android, Safari) ===== */
      navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        .then(function (s) {
          stream = s;
          video.srcObject = s;

          var detector = new BarcodeDetector({ formats: ['code_128', 'qr_code', 'data_matrix', 'ean_13'] });
          var startTime = Date.now();

          scanInterval = setInterval(function () {
            detector.detect(video).then(function (barcodes) {
              if (barcodes.length > 0) {
                var raw = barcodes[0].rawValue || '';
                var cleaned = raw.replace(/[^a-zA-Z0-9]/g, '');
                if (cleaned.length >= 5) {
                  trackEvent('cert_verify_scan_success', {
                    scan_duration_ms: Date.now() - startTime,
                    barcode_format: barcodes[0].format
                  });
                  self.numberInput.value = cleaned;
                  var detected = detectLab(cleaned);
                  if (detected) self.labSelect.value = detected;
                  cleanup();
                  self._handleSubmit();
                }
              }
            }).catch(function () {});
          }, 500);

          scanTimeout = setTimeout(function () {
            overlay.querySelector('.cert-verify__scanner-hint').textContent = t('sc.slow');
          }, 15000);
        })
        .catch(function () {
          cleanup();
          alert(t('sc.camera'));
        });
    } else {
      /* ===== FALLBACK PATH: html5-qrcode (Chrome desktop, Firefox, etc.) =====
         Loads ZXing-based html5-qrcode from CDN on demand.
         It handles its own getUserMedia, so we skip the native video element. */
      video.style.display = 'none';
      overlay.querySelector('.cert-verify__scanner-crosshair').style.display = 'none';
      overlay.querySelector('.cert-verify__scanner-hint').textContent = t('sc.loading');

      var fbScript = document.createElement('script');
      fbScript.src = 'https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js';
      fbScript.onload = function () {
        var readerId = 'cert-fallback-reader';
        var readerDiv = document.createElement('div');
        readerDiv.id = readerId;
        readerDiv.style.width = '100%';
        readerDiv.style.height = '100%';
        overlay.querySelector('.cert-verify__scanner-container').appendChild(readerDiv);

        overlay.querySelector('.cert-verify__scanner-hint').textContent =
          t('sc.hint');

        var startTime = Date.now();
        var qr = new Html5Qrcode(readerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.DATA_MATRIX,
            Html5QrcodeSupportedFormats.EAN_13
          ]
        });
        self._fallbackReader = qr;

        qr.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: function (vw, vh) {
              var s = Math.min(vw, vh);
              return { width: Math.floor(s * 0.8), height: Math.floor(s * 0.45) };
            }
          },
          function (decodedText) {
            var cleaned = decodedText.replace(/[^a-zA-Z0-9]/g, '');
            if (cleaned.length >= 5) {
              trackEvent('cert_verify_scan_success', {
                scan_duration_ms: Date.now() - startTime,
                barcode_format: 'html5-qrcode-fallback'
              });
              self.numberInput.value = cleaned;
              var detected = detectLab(cleaned);
              if (detected) self.labSelect.value = detected;
              cleanup();
              self._handleSubmit();
            }
          },
          function () { /* ignore per-frame decode misses */ }
        ).catch(function () {
          overlay.querySelector('.cert-verify__scanner-hint').textContent =
            t('sc.noCamera');
        });

        scanTimeout = setTimeout(function () {
          overlay.querySelector('.cert-verify__scanner-hint').textContent = t('sc.slow');
        }, 15000);
      };
      fbScript.onerror = function () {
        overlay.querySelector('.cert-verify__scanner-hint').textContent =
          t('sc.noLoad');
      };
      document.head.appendChild(fbScript);
    }
  };

  /* ---------------------------------------------------------------
     FAQ TOGGLE
     --------------------------------------------------------------- */
  function initFAQ() {
    var items = document.querySelectorAll('.cert-verify__faq-item');
    items.forEach(function (item) {
      var btn = item.querySelector('.cert-verify__faq-question');
      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var isOpen = item.hasAttribute('open');
          if (isOpen) {
            item.removeAttribute('open');
          } else {
            item.setAttribute('open', '');
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------
     INIT
     --------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    var form = document.getElementById('cert-verify-form');
    if (!form) return;

    new CertificateVerifier();
    initFAQ();

    /* Hide scan button on desktop (only useful on mobile) */
    var scanBtn = document.getElementById('cert-scan-btn');
    if (scanBtn && !navigator.mediaDevices) {
      scanBtn.style.display = 'none';
    }
  });

})();

